import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertCircle, ArrowLeft, Mail, CheckCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      if (redirect) {
        navigate(redirect);
      } else if (result.user.role === 'admin') {
        navigate('/dashboard');
      } else if (result.user.role === 'delivery') {
        navigate('/delivery');
      } else {
        navigate('/customer/dashboard');
      }
    } else {
      setError(result.error);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    setForgotError('');
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotSuccess(true);
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotDialog = () => {
    setShowForgot(false);
    setForgotEmail('');
    setForgotSuccess(false);
    setForgotError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-4" data-testid="back-to-home-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
        </Button>

        <div className="text-center mb-6">
          <img
            src="/at1s-logo-solid.png"
            alt="AirTech OneSolutions"
            className="h-24 w-auto mx-auto mb-2 object-contain"
          />
        </div>

        <Card className="shadow-lg border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl font-bold text-center">Welcome Back</CardTitle>
            <CardDescription className="text-center text-sm">Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="you@example.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" placeholder="Enter your password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={() => { setForgotEmail(email); setShowForgot(true); }}
                  className="text-xs text-sky-600 hover:text-sky-700 hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 font-semibold"
                disabled={loading} data-testid="login-submit-btn">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-5 text-center text-sm">
              <span className="text-gray-500">Don't have an account? </span>
              <Link to="/register" className="text-sky-600 hover:text-sky-700 font-semibold" data-testid="register-link">Sign up</Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgot} onOpenChange={closeForgotDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-sky-600" /> Reset Password
            </DialogTitle>
            <DialogDescription>
              Enter your email address and we'll notify the admin to reset your password.
            </DialogDescription>
          </DialogHeader>

          {forgotSuccess ? (
            <div className="py-4">
              <div className="flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Request Submitted</p>
                  <p className="text-sm text-gray-500 mt-1">
                    An admin will review your request and reset your password. You'll be contacted at <strong>{forgotEmail}</strong> once it's done.
                  </p>
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button onClick={closeForgotDialog} className="w-full" data-testid="forgot-done-btn">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              {forgotError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{forgotError}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input id="forgot-email" type="email" placeholder="you@example.com" value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)} required data-testid="forgot-email-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeForgotDialog}>Cancel</Button>
                <Button type="submit" disabled={forgotLoading}
                  className="bg-gradient-to-r from-sky-500 to-teal-500" data-testid="forgot-submit-btn">
                  {forgotLoading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Submitting...</> : 'Submit Request'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
